import type { ReactElement, ReactNode } from 'react';
import { cn } from '../lib/cn';

export type FieldMessageProps = {
  /** Validation message (string or rich node). Takes precedence over `hint`. */
  error?: ReactNode;
  /** Non-error helper text under the control. */
  hint?: ReactNode;
  className?: string;
};

/**
 * Hint / validation line under a control.
 *
 * Always reserves one line of `text-ds-xs` (12px × 1.45) so showing or hiding
 * validation does not shift the form layout. Multi-line messages may still grow
 * the slot slightly; the common single-line case is stable.
 */
export function FieldMessage({
  error,
  hint,
  className,
}: FieldMessageProps): ReactElement {
  const hasError = error != null && error !== false && error !== '';
  const hasHint = !hasError && hint != null && hint !== '';

  return (
    <div
      className={cn(
        // Match --text-ds-xs (12px) × --text-ds-xs--line-height (1.45) ≈ 17.4px
        'min-h-[1.125rem] text-ds-xs leading-[1.45]',
        className,
      )}
    >
      {hasError ? (
        <p className="text-ds-danger" role="alert">
          {error}
        </p>
      ) : hasHint ? (
        <p className="text-ds-muted">{hint}</p>
      ) : null}
    </div>
  );
}
